/**
 * Location Combo Select.
 *
 * Reusable location selector that wraps ComboSelect with business
 * location and storage area data. Supports searching, selecting from
 * existing locations, and creating new freeform text entries.
 *
 * The underlying asset `location` field is plain text — this component
 * just provides a smarter input experience by surfacing locations
 * defined in Settings > Locations alongside their storage areas.
 */

import { useMemo } from "react";
import { MapPin, Grid3X3 } from "lucide-react";
import ComboSelect from "@/components/ui/ComboSelect";
import type { ComboOption } from "@/components/ui/ComboSelect";
import { useBusinessLocations } from "@/features/locations/api";
import { useStorageAreas } from "@/features/yards/api";

interface LocationComboSelectProps {
  /** Currently selected location text. */
  value: string;
  /** Called when the user selects or creates a location. */
  onChange: (value: string) => void;
  /** Label text above the input. */
  label?: string;
  /** Placeholder shown when nothing is selected. */
  placeholder?: string;
  /** Whether the field is required. */
  required?: boolean;
  /** Whether the component is disabled. */
  disabled?: boolean;
  /** Additional class name. */
  className?: string;
}

/**
 * Formats a location_type slug into a human-readable label.
 * e.g. "parking_lot" → "Parking lot", "warehouse" → "Warehouse"
 */
function formatLocationType(type: string): string {
  return type.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function LocationComboSelect({
  value,
  onChange,
  label,
  placeholder = "Search or type a location...",
  required = false,
  disabled = false,
  className,
}: LocationComboSelectProps) {
  const { data: businessLocations } = useBusinessLocations();
  const { data: storageAreas } = useStorageAreas();

  const options: ComboOption[] = useMemo(() => {
    const result: ComboOption[] = [];

    const inventoryLocations = (businessLocations ?? []).filter(
      (bl) => bl.stores_inventory && bl.is_active,
    );

    for (const bl of inventoryLocations) {
      result.push({
        value: bl.name,
        label: bl.name,
        sublabel: formatLocationType(bl.location_type),
        icon: <MapPin className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />,
      });

      const blAreas = (storageAreas ?? []).filter(
        (sa) => sa.business_location_id === bl.id,
      );
      for (const sa of blAreas) {
        if (sa.locations && sa.locations.length > 0) {
          for (const loc of sa.locations) {
            result.push({
              value: `${bl.name} — ${sa.label} — ${loc.label}`,
              label: `${bl.name} — ${sa.label} — ${loc.label}`,
              sublabel: formatLocationType(sa.section_type),
              icon: (
                <Grid3X3 className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
              ),
            });
          }
        } else {
          result.push({
            value: `${bl.name} — ${sa.label}`,
            label: `${bl.name} — ${sa.label}`,
            sublabel: formatLocationType(sa.section_type),
            icon: (
              <Grid3X3 className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
            ),
          });
        }
      }
    }

    return result;
  }, [businessLocations, storageAreas]);

  return (
    <ComboSelect
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      label={label}
      required={required}
      disabled={disabled}
      clearable
      creatable
      className={className}
    />
  );
}

export default LocationComboSelect;
